import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import * as BABYLON from "@babylonjs/core";
import { v4 as uuidv4 } from 'uuid';

import { SceneContext } from './context';
import { KeyboardContext } from './keyboard-context';
import { SocketContext } from './socketio';
import { SKYBOX_SIZE } from './common';


let BASE_PATH = '/island/';
let SODABITS_BASE_PATH = '/sodabits/';
const DEFAULT_POSITION = [26, 2.5, -80];

window.BABYLON = BABYLON;

const faceUV = [
  new BABYLON.Vector4(0,0,1,1),
  new BABYLON.Vector4(0,0,1,1),
  new BABYLON.Vector4(0,0,0,1),
  new BABYLON.Vector4(0,0,0,1),
  new BABYLON.Vector4(0,0,0,1),
  new BABYLON.Vector4(0,0,0,1),
];

const Sodabit = ({sodabit_id=123, x, y, z, enablePhysics=false, cameraLock=false}) => {
  const { cameraRef, sceneRef } = useContext(SceneContext);
  const keys = useContext(KeyboardContext);
  const { socket, connected } = useContext(SocketContext);

  let sodabitRef = useRef();

  useEffect(() => {
    let scene = sceneRef.current;

    if (!sodabit_id) { sodabit_id=123; }

    let sodabit =  BABYLON.MeshBuilder.CreateBox("sodabit", {height:4, width: 2, depth: 0, wrap: true, faceUV}, scene);
    let mat = new BABYLON.StandardMaterial("", scene);
    mat.diffuseTexture  = new BABYLON.Texture(SODABITS_BASE_PATH + `/${sodabit_id}.png`, scene);
    mat.emissiveTexture = new BABYLON.Texture(SODABITS_BASE_PATH + `/${sodabit_id}.png`, scene);
    sodabit.material = mat;
    sodabit.position.y = 20;
    sodabit.position.x = 0

    sodabitRef.current = sodabit;
  }, [ sceneRef ]);

  useEffect(() => {
    if (!connected) { return; }
    if (!enablePhysics) { return; }

    const cb = () => {
      let sodabit = sodabitRef.current;
      if (!sodabit) { return; }

      let position = {
        x: sodabit.position.x,
        y: sodabit.position.y,
        z: sodabit.position.z,
        sodabit_id,
      };
      socket.emit('position', position);
    };
    socket.on('get-position', cb);

    return () => socket.off('get-position', cb);
  }, [connected, socket, enablePhysics, sodabit_id]);

  useEffect(() => {
    let scene = sceneRef.current;
    let sodabit = sodabitRef.current;
    if (!sodabit_id) { sodabit_id=123; }

    let mat = new BABYLON.StandardMaterial("", scene);
    mat.diffuseTexture  = new BABYLON.Texture(SODABITS_BASE_PATH + `/${sodabit_id}.png`, scene);
    mat.emissiveTexture = new BABYLON.Texture(SODABITS_BASE_PATH + `/${sodabit_id}.png`, scene);

    mat.diffuseTexture.hasAlpha = true;
    mat.emissiveTexture.hasAlpha = true;

    sodabit.material.dispose();
    sodabit.material = mat;

  }, [ sceneRef, sodabitRef, sodabit_id ]);

  useEffect(() => {
    const scene = sceneRef.current;
    const sodabit = sodabitRef.current;

    if (!enablePhysics) { return; }
    if (!sodabit) { return; }

    sodabit.physicsImpostor = new BABYLON.PhysicsImpostor(sodabit, BABYLON.PhysicsImpostor.BoxImpostor, { mass: 1, friction: 1, restitution: 0.0, angularDamping:100 }, scene);
    window.sodabit = sodabit;
  }, [enablePhysics, sceneRef, sodabitRef ]);

  useEffect(() => {
    const camera = cameraRef.current;
    const scene = sceneRef.current;
    const sodabit = sodabitRef.current;

    if (!cameraLock) { return; }
    if (!sodabit) { return; }

    camera.lockedTarget = sodabit;

  }, [cameraLock, cameraRef, sceneRef, sodabitRef ]);

  useEffect(() => {

    const camera = cameraRef.current;
    const scene = sceneRef.current;
    const engine = scene.getEngine();

    const update = () => {
      const sodabit = sodabitRef.current;
      if (!sodabit) { return; }

      const cameraForwardRayPosition = camera.getForwardRay().direction;
      const cameraForwardRayPositionWithoutY = new BABYLON.Vector3(cameraForwardRayPosition.x, 0, cameraForwardRayPosition.z);
      const speed = keys.shift ? 1.5 : 1;

      if (enablePhysics) {
        if (keys.a) {
          sodabit.locallyTranslate(new BABYLON.Vector3(-speed, 0, 0));
        }
        if (keys.d) {
          sodabit.locallyTranslate(new BABYLON.Vector3(speed, 0, 0));
        }
        if (keys.w) {
          sodabit.position = sodabit.position.add(new BABYLON.Vector3(cameraForwardRayPosition.x * speed, 0, cameraForwardRayPosition.z * speed));
        }
        if (keys.s) {
          sodabit.position = sodabit.position.add(new BABYLON.Vector3(-cameraForwardRayPosition.x * speed, 0, -cameraForwardRayPosition.z * speed));
        }
      }


      if (sodabit.position.y < 0) {
        sodabit.position = new BABYLON.Vector3(...DEFAULT_POSITION);
      }

      const xSign = sodabit.position.x > 0 ? 1 : -1;
      const zSign = sodabit.position.z > 0 ? 1 : -1;
      const skyboxLimit =  SKYBOX_SIZE/2 - 50;
      if (Math.abs(sodabit.position.x) > skyboxLimit) {
        sodabit.position.x = skyboxLimit * xSign;
      }
      if (Math.abs(sodabit.position.z) > skyboxLimit) {
        sodabit.position.z = skyboxLimit * zSign;
      }

      sodabit.lookAt(sodabit.position.add(cameraForwardRayPositionWithoutY), 0, 0, 0);
    };

    engine.runRenderLoop(update);

  }, [ cameraRef, enablePhysics, sceneRef, sodabitRef, keys ]);

  useEffect(() => {
    let sodabit = sodabitRef.current;
    if (!sodabit) { return; }

    sodabit.position.x = x;
    sodabit.position.y = y;
    sodabit.position.z = z;
  }, [x,y,z]);

  useEffect(() => {
    return () => {
      sodabitRef.current.dispose();
      sodabitRef.current = null;
    };
  }, [sodabitRef]);

  return null;
};


export default Sodabit;
